"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import {
  BadgeCheckIcon,
  MailCheckIcon,
  PhoneCallIcon,
  Building2Icon,
  TrendingUpIcon,
  ArrowLeftIcon,
} from "lucide-react";

import { CDN_ASSETS } from "@/lib/cdn";

export function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="bg-white min-h-screen px-4 py-4 sm:px-8 sm:py-5">
      {/* Top bar */}
      <header className="mx-auto flex max-w-295 flex-wrap items-start justify-between gap-3">
        {/* prefetch=false: this logo is visible while logged out, and Link's
            default auto-prefetch would otherwise warm the Router Cache with
            proxy.ts's logged-out redirect-to-login for /home, served stale
            (staleTimes.static, 5min) to the real post-login navigation. */}
        <Link href="/home" prefetch={false} className="animate-rise flex flex-col items-start">
          <Image
            src={CDN_ASSETS.logo}
            alt="Infynd"
            width={205}
            height={70}
            priority
            className="h-11 w-auto sm:h-12"
          />
          {/* The tagline is the first thing to go when width is tight */}
          <span className="pl-2 hidden text-[13px] leading-none text-muted-foreground sm:block">
            Empower Your Pipeline. Simplify Your Prospecting!
          </span>
        </Link>

        {/*
         * Get Support and the language switcher — both disabled, not just
         * unwired. Neither has anything real behind it yet: no support
         * channel is chosen (the ticket-type and Intercom-token plumbing
         * elsewhere in the codebase are unused), and no i18n setup exists
         * anywhere in the app (no locale routing, no translations). Left
         * commented rather than rendered-but-inert — nothing here for a
         * click to do. Restore once a real destination exists for each.
         */}
        {/* <div className="animate-rise flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            aria-label="Get Support"
            className="hover:shadow-soft inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-background/70 px-3 text-sm font-medium text-foreground/80 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-background sm:px-4"
          >
            <HeadphonesIcon className="size-4 shrink-0" />
            <span className="hidden sm:inline">Get Support</span>
          </button>
          <button
            type="button"
            aria-label="Change language"
            className="hover:shadow-soft inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-background/70 px-3 text-sm font-medium text-foreground/80 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-background sm:px-4"
          >
            <GlobeIcon className="size-4 shrink-0" />
            <span className="hidden sm:inline">English</span>
            <ChevronDownIcon className="size-4 opacity-60" />
          </button>
        </div> */}
      </header>

      {/* Main split card */}
      {/*
       * From lg up, height comes from the card and never from the form inside
       * it, so login (2 fields) and register (5) are identical and navigating
       * between them doesn't resize anything. 44rem is the floor that fits the
       * taller register form; above that the card tracks the viewport. Below lg
       * the card sizes to its content — a phone can't spare the empty space.
       */}
      <div className="animate-rise mx-auto mt-5 grid max-w-295 gap-5 rounded-[28px] border border-brand/20 bg-background/50 p-2.5 shadow-[0_30px_80px_-30px_#ff1a26/0.25] hover:shadow-[0_30px_80px_-30px_#ff1a26/0.35] backdrop-blur-xl transition-shadow duration-300 sm:p-3 lg:h-[max(44rem,calc(100svh-8rem))] lg:grid-cols-[1.12fr_0.88fr]">
        {/* Showcase panel */}
        {/*
         * Three bands: copy, stage, partners. Copy and partners are fixed and
         * the stage absorbs whatever height is left, so the partner strip stays
         * pinned in view at any viewport height and the panel itself never
         * scrolls — the card's own height is the only scroll contract here.
         */}
        <section className="bg-auth-panel-gradient relative hidden overflow-hidden rounded-[22px] lg:flex lg:flex-col">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="animate-blob absolute -top-24 -left-16 size-104 bg-foreground/6 blur-3xl" />
            <div className="animate-blob absolute -right-24 bottom-0 size-112 bg-foreground/4 blur-3xl [animation-delay:-6s] animation-duration-[24s]" />
          </div>

          {/* Copy */}
          <div className="relative shrink-0 px-8 pt-8">
            <p className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
              Sales Intelligence Platform
            </p>
            <h2 className="mt-2.5 max-w-sm text-[26px] leading-tight font-semibold tracking-tight text-foreground">
              Search prospects, verify contacts, close more deals.
            </h2>
          </div>

          {/* Stage — product screenshot with the live insight panel floating over it */}
          <div className="relative min-h-0 flex-1">
            {/*
             * Inset on both sides and vertically centred: the mockup stays
             * whole — no edge runs under the panel border — and the height the
             * viewport leaves over splits above and below it instead of
             * pooling into one dead gap.
             */}
            <div className="absolute inset-x-8 top-1/2 -translate-y-1/2">
              <div className="animate-float-slow overflow-hidden rounded-2xl border border-border bg-background/85 shadow-2xl backdrop-blur-md -rotate-2">
                {/* browser chrome */}
                <div className="flex shrink-0 items-center gap-3 border-b border-border/60 bg-muted/40 px-4 py-2.5">
                  <span className="flex gap-1.5">
                    <span className="size-2.5 rounded-full bg-foreground/20" />
                    <span className="size-2.5 rounded-full bg-foreground/15" />
                    <span className="size-2.5 rounded-full bg-foreground/10" />
                  </span>
                  <span className="mx-auto rounded-md bg-muted/70 px-6 py-0.5 text-[10px] text-muted-foreground">
                    app.infynd.io
                  </span>
                </div>

                <div className="p-5">
                  {/* nav */}
                  <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                      <LogoMark small />
                      Infynd
                    </span>
                    <span className="ml-3">Home</span>
                    <span className="rounded-md bg-background px-2 py-1 font-medium text-foreground shadow-sm">
                      Companies
                    </span>
                    <span>People</span>
                    <span>Lists</span>
                    <span>Enrichment</span>
                  </div>

                  <p className="mt-6 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="flex size-5 items-center justify-center rounded-full bg-background shadow-sm">
                      <ArrowLeftIcon className="size-3" />
                    </span>
                    Back To Companies
                  </p>

                  <div className="mt-4 flex items-center gap-3">
                    <span className="flex size-11 items-center justify-center overflow-hidden rounded-xl bg-muted">
                      <LogoMark />
                    </span>
                    <div>
                      <p className="text-[10px] text-muted-foreground">
                        SaaS Company
                      </p>
                      <p className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
                        Infynd
                        <BadgeCheckIcon className="size-5 text-green-500" />
                      </p>
                    </div>
                  </div>

                  {/* tabs */}
                  <div className="mt-5 flex items-center gap-2 rounded-xl border border-border/60 bg-muted/40 p-1.5 text-[11px]">
                    <span className="rounded-lg bg-muted px-3 py-1.5 font-medium">
                      Overview
                    </span>
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-muted-foreground">
                      Employees
                      <span className="rounded-full bg-muted px-1.5 text-[9px] font-semibold text-foreground/70">
                        5
                      </span>
                    </span>
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-muted-foreground">
                      Emails
                      <span className="rounded-full bg-muted px-1.5 text-[9px] font-semibold text-foreground/70">
                        13
                      </span>
                    </span>
                    <span className="px-3 py-1.5 text-muted-foreground">
                      Phones
                    </span>
                  </div>

                  {/* stats */}
                  <div className="mt-5">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      Contact Data Overview
                      <span className="rounded-md bg-muted px-1.5 py-0.5 text-[9px] font-semibold text-foreground/70">
                        Beta
                      </span>
                    </p>
                    <p className="mt-3 text-[10px] text-muted-foreground">
                      Last Update 3 days Ago
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-3xl font-semibold tracking-tight">
                      11,656,060
                      <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        +456
                      </span>
                    </p>
                  </div>

                  {/*
                   * AI assistant — the product doesn't ship this yet, so it
                   * stays out of the shot rather than promising it on the login
                   * page. Restore this block once the feature is live; it needs
                   * `mt-auto` plus a flex parent to sit on the bottom edge.
                   */}
                  {/* <div className="mt-auto flex shrink-0 items-center gap-2 rounded-2xl border border-border bg-background p-2.5 shadow-xl">
                    <p className="flex-1 truncate pl-2 text-[11px] text-muted-foreground">
                      Hey there! How can I help you today?
                    </p>
                    <span className="flex size-6 items-center justify-center rounded-lg border border-border/60">
                      <PlusIcon className="size-3" />
                    </span>
                    <span className="flex items-center gap-1 rounded-lg border border-border/60 px-2 py-1 text-[10px]">
                      <SlidersHorizontalIcon className="size-3 text-muted-foreground" />{" "}
                      Lead Case
                    </span>
                    <MicIcon className="size-3.5 text-muted-foreground" />
                    <span className="flex size-6 items-center justify-center rounded-full bg-foreground text-background">
                      <ArrowUpIcon className="size-3" />
                    </span>
                  </div> */}
                </div>
              </div>

              {/* Inside the centring wrapper, so it tracks the mockup's own
                  bottom-right corner rather than the stage's. */}
              <InsightPanel />
            </div>
          </div>

          {/* Partners */}
          <PartnerStrip />
        </section>

        {/* Form panel — from lg up it scrolls inside the fixed card instead of stretching it */}
        <section className="flex items-center justify-center rounded-[22px] bg-card px-5 py-8 shadow-sm sm:px-10 sm:py-10 lg:overflow-y-auto">
          {children}
        </section>
      </div>
    </main>
  );
}

/*
 * The floating panel over the mockup. It cycles the four numbers the product
 * actually leads with, so the animation is showing the app rather than
 * decorating the page. Cross-fade only — the panel keeps its footprint and
 * position so nothing under it appears to move.
 *
 * `bars` holds Tailwind height classes rather than numbers: Tailwind only emits
 * a class it can see as a literal string in the source, so a computed
 * `h-[${n}px]` compiles to nothing — the class has to be the datum itself.
 * Arbitrary px because the 5-27px range doesn't land on the spacing scale; the
 * track is h-8 (32px), so 27 is the ceiling before a bar starts clipping.
 */
const INSIGHTS = [
  {
    icon: MailCheckIcon,
    label: "Verified Emails",
    value: "8,023,030",
    delta: "+12.4%",
    caption: "Bounce-tested in the last 30 days",
    bars: [
      "h-[5px]", "h-[9px]", "h-[6px]", "h-[12px]", "h-[8px]", "h-[16px]",
      "h-[11px]", "h-[18px]", "h-[14px]", "h-[22px]", "h-[17px]", "h-[26px]",
    ],
  },
  {
    icon: PhoneCallIcon,
    label: "Direct Dials",
    value: "3,633,030",
    delta: "+8.1%",
    caption: "Mobile-first, connected numbers",
    bars: [
      "h-[8px]", "h-[6px]", "h-[11px]", "h-[9px]", "h-[14px]", "h-[10px]",
      "h-[17px]", "h-[13px]", "h-[19px]", "h-[16px]", "h-[23px]", "h-[21px]",
    ],
  },
  {
    icon: Building2Icon,
    label: "Companies Tracked",
    value: "1,204,880",
    delta: "+3,120",
    caption: "Firmographics refreshed weekly",
    bars: [
      "h-[12px]", "h-[10px]", "h-[15px]", "h-[13px]", "h-[11px]", "h-[18px]",
      "h-[15px]", "h-[21px]", "h-[17px]", "h-[24px]", "h-[20px]", "h-[27px]",
    ],
  },
] as const;

const INSIGHT_MS = 4200;

function InsightPanel() {
  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(
      () => setActive((i) => (i + 1) % INSIGHTS.length),
      INSIGHT_MS
    );
    return () => clearInterval(id);
  }, []);

  // Hangs just past the mockup's bottom-right corner: it reads as a panel
  // floating above the screenshot rather than a tile pasted inside it, and the
  // mockup's right side is empty there so nothing legible gets covered.
  return (
    <div className="animate-float absolute -right-3 -bottom-9 z-20 w-56 [animation-delay:-2s]">
      <div className="relative overflow-hidden rounded-2xl border border-brand/30 bg-background/95 shadow-[0_24px_60px_-20px_#ff1a26/0.25] backdrop-blur-xl hover:shadow-[0_24px_60px_-20px_#ff1a26/0.35] transition-shadow duration-300">
        {/* progress — reads as "live", and telegraphs the next swap */}
        <div className="absolute inset-x-0 top-0 h-1 bg-brand/10">
          {/* Remounted on every swap (via key) so the animation replays. */}
          <div
            key={active}
            className="animate-sweep h-full origin-left bg-linear-to-r from-brand/60 via-brand to-brand/40"
          />
        </div>

        {/* Grid-stacked so the panel is as tall as its tallest slide and never resizes mid-cycle */}
        <div className="grid p-4">
          {INSIGHTS.map((insight, i) => (
            <div
              key={insight.label}
              aria-hidden={i !== active}
              className={`col-start-1 row-start-1 transition-opacity duration-500 ${
                i === active ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="flex size-7 items-center justify-center rounded-lg bg-muted">
                  <insight.icon className="size-3.5 text-foreground/70" />
                </span>
                <p className="text-[11px] font-medium text-muted-foreground">
                  {insight.label}
                </p>
              </div>

              <p className="mt-3 text-2xl leading-none font-semibold tracking-tight">
                {insight.value}
              </p>

              <p className="mt-2 flex items-center gap-1 text-[10px] font-medium text-foreground/70">
                <TrendingUpIcon className="size-3" />
                {insight.delta}
              </p>

              <div className="mt-3 flex h-8 items-end gap-0.75">
                {insight.bars.map((barHeight, b) => (
                  <span
                    key={b}
                    className={`w-1.25 rounded-sm bg-foreground/25 ${barHeight}`}
                    style={{
                      animation: `slideUp 0.6s ease-out ${b * 30}ms both`,
                    }}
                  />
                ))}
              </div>

              <p className="mt-3 text-[10px] leading-snug text-muted-foreground">
                {insight.caption}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Steppers, deliberately quiet — they mark position, they aren't a control surface */}
      <div className="mt-3 flex justify-center gap-1.5">
        {INSIGHTS.map((insight, i) => (
          <button
            key={insight.label}
            type="button"
            onClick={() => setActive(i)}
            aria-label={insight.label}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === active ? "w-5 bg-brand" : "w-1 bg-foreground/25"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

const PARTNER_LOGOS = [
  { src: CDN_ASSETS.partners.britannia, alt: "Britannia" },
  { src: CDN_ASSETS.partners.cvlibrary, alt: "CV-Library" },
  { src: CDN_ASSETS.partners.deliveroo, alt: "Deliveroo" },
  { src: CDN_ASSETS.partners.experian, alt: "Experian" },
  { src: CDN_ASSETS.partners.silveyfleet, alt: "Silver Fleet", small: true },
  { src: CDN_ASSETS.partners.sony, alt: "Sony", small: true },
] as const;

function PartnerStrip() {
  return (
    <div className="relative z-10 shrink-0 border-t border-border/60 bg-background/30 px-8 py-5 backdrop-blur-sm">
      <div className="flex items-center justify-center gap-3 opacity-60">
        <span className="h-px w-10 bg-linear-to-r from-transparent via-border to-transparent" />
        <p className="text-center text-[10px] font-semibold tracking-[0.3em] text-muted-foreground uppercase">
          Trusted Partners
        </p>
        <span className="h-px w-10 bg-linear-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="mt-4 opacity-60 transition-opacity duration-500 hover:opacity-100">
        <PartnerCarousel />
      </div>
    </div>
  );
}

// Tripled so the strip always has slides to slide into on both sides; once the
// index rolls past the second copy it snaps back a copy's width — with the
// transition switched off for that one frame — so the loop reads as endless.
const VISIBLE_SLIDES = 3;
const LOOPED_LOGOS = [...PARTNER_LOGOS, ...PARTNER_LOGOS, ...PARTNER_LOGOS];

/*
 * Track position per index, as static classes for the same reason `bars` above
 * holds classes: a template-built `translate-x-[...]` is invisible to Tailwind.
 *
 * The track is w-[600%] (LOOPED_LOGOS.length / VISIBLE_SLIDES = 18 / 3) and each
 * slide is w-[calc(100%/18)] of it, so one step is 1/18th of the track's own
 * width — which is what a translate-x percentage resolves against. The literal
 * 18s and the 600% must track those two constants; they cannot be interpolated,
 * so `assertCarouselGeometry` below fails loudly in dev if anyone edits
 * PARTNER_LOGOS without revisiting them.
 *
 * Indices 0-12 are every value reachable: the interval increments without bound
 * but the snap-back effect resets to 6 as soon as index hits 12.
 */
const SLIDE_OFFSETS = [
  "translate-x-0",
  "translate-x-[calc(-100%*1/18)]",
  "translate-x-[calc(-100%*2/18)]",
  "translate-x-[calc(-100%*3/18)]",
  "translate-x-[calc(-100%*4/18)]",
  "translate-x-[calc(-100%*5/18)]",
  "translate-x-[calc(-100%*6/18)]",
  "translate-x-[calc(-100%*7/18)]",
  "translate-x-[calc(-100%*8/18)]",
  "translate-x-[calc(-100%*9/18)]",
  "translate-x-[calc(-100%*10/18)]",
  "translate-x-[calc(-100%*11/18)]",
  "translate-x-[calc(-100%*12/18)]",
] as const;

if (process.env.NODE_ENV !== "production") {
  const steps = PARTNER_LOGOS.length * 2 + 1;
  if (LOOPED_LOGOS.length !== 18 || SLIDE_OFFSETS.length !== steps) {
    console.error(
      `[auth-layout] PartnerCarousel geometry drifted: ${LOOPED_LOGOS.length} looped slides ` +
        `and ${SLIDE_OFFSETS.length} offsets. SLIDE_OFFSETS needs ${steps} entries, and the ` +
        `hardcoded 18 / w-[600%] in this file need to match the new slide count.`
    );
  }
}

function PartnerCarousel() {
  const [index, setIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(true);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setIndex((prev) => prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (index < PARTNER_LOGOS.length * 2) return;
    const timeout = setTimeout(() => {
      setIsTransitioning(false);
      setIndex(PARTNER_LOGOS.length);
    }, 700);
    return () => clearTimeout(timeout);
  }, [index]);

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={`flex w-[600%] items-center ${SLIDE_OFFSETS[Math.min(index, SLIDE_OFFSETS.length - 1)]} ${
          isTransitioning ? "transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]" : ""
        }`}
      >
        {LOOPED_LOGOS.map((logo, i) => {
          const centerPosition = index + Math.floor(VISIBLE_SLIDES / 2);
          const distance = Math.abs(i - centerPosition);
          const scale = distance === 0 ? "scale-110" : distance === 1 ? "scale-90" : "scale-75";
          const opacity = distance === 0 ? "opacity-100" : distance === 1 ? "opacity-70" : "opacity-40";

          return (
            <div
              key={i}
              className={`flex w-[calc(100%/18)] shrink-0 items-center justify-center transition-all duration-500 ${scale} ${opacity}`}
            >
              <Image
                src={logo.src}
                alt={logo.alt}
                width={110}
                height={32}
                className={`h-6 w-auto object-contain brightness-0 ${"small" in logo && logo.small ? "scale-90" : "scale-125"}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LogoMark({ small }: { small?: boolean }) {
  return (
    <Image
      src={CDN_ASSETS.favicon}
      alt="Infynd"
      width={24}
      height={24}
      priority
      className={
        small ? "size-4 rounded-[3px]" : "size-6 rounded-[5px] sm:size-7"
      }
    />
  );
}
